import { createShellStore, type ShellStoreApi, type WorkShapes } from "@hakili/app-shell";

/**
 * `P0-UIK-016`: the client's shell store, and the item's stated test.
 *
 * 08 states it as *"filling a form, navigating back and returning preserves the
 * input"*, and 4.6.4 says why it is the shell's job rather than a screen's:
 * *"Back navigation never loses a partially filled form, because the form state
 * is in the shell store."*
 *
 * **Danger sentence.** This is the failure that loses a customer, not a
 * developer. A buyer on 3G fills three fields of an eligibility form, taps a
 * vehicle to check its price, comes back , and on a phone "comes back" is the
 * hardware back button, pressed constantly , and finds the form empty. He does
 * not report a bug. He decides the site is broken and stops, and there is no
 * signal anywhere that it happened.
 *
 * **`localStorage`, and 4.6.5 names the reason.** *"The store persists to
 * `sessionStorage` per tab, and the client app persists to `localStorage`
 * because a client loses his connection mid-form and comes back on the same
 * handset."* The console and the bank take `sessionStorage`, because those are
 * shared desktops and a half-written note surviving into the next person's
 * session is a disclosure. A buyer's own handset is the opposite case.
 *
 * **One persona, deliberately.** The client has no views and no personas , one
 * buyer, one set of screens , so the union is a single value rather than an
 * abstraction the app does not need. It costs nothing and keeps the shared
 * store's shape identical across the three apps, which is `DECISION ADDED
 * A35`'s whole point.
 */

export type PersonaClient = "client";

export interface ClientWork extends WorkShapes {
  readonly dossier: {
    /** The eligibility form, field by field, as typed , never as validated. */
    eligibilite: Readonly<Record<string, string>>;
    /** A monthly payment the buyer is exploring, before he commits to it. */
    mensualiteSouhaitee: string;
    /** Which offer he is looking at, so returning to the list does not lose the comparison. */
    offreConsultee: string;
  };
  /** A vehicle he is considering: filters and notes taken while browsing. */
  readonly vehicle: { note: string };
  readonly partner: { note: string };
}

export type ClientStore = ShellStoreApi<PersonaClient, ClientWork>;

const localStorageOrMemory = (): Storage => {
  // Absent in a test or a server render. An in-memory map behaves identically
  // for one session, and throwing would make the shell unrenderable outside a
  // browser for no benefit.
  if (typeof globalThis.localStorage !== "undefined") return globalThis.localStorage;
  const seen = new Map<string, string>();
  return {
    getItem: (name: string) => seen.get(name) ?? null,
    setItem: (name: string, value: string) => {
      seen.set(name, value);
    },
    removeItem: (name: string) => {
      seen.delete(name);
    },
    clear: () => {
      seen.clear();
    },
    key: (index: number) => [...seen.keys()][index] ?? null,
    get length() {
      return seen.size;
    },
  };
};

export function creerClientStore(storage: Storage = localStorageOrMemory()): ClientStore {
  return createShellStore<PersonaClient, ClientWork>({
    personas: ["client"],
    initialPersona: "client",
    landingScreen: () => "/",
    storage,
    name: "hakili.client",
  });
}
